import React, { useEffect, useState } from "react";
import { Head } from "@unhead/react";
import { Link } from "react-router-dom";
import api from "../api";

const OPTIONS = [30, 90, 180, 365];

function signedPct(value) {
  const n = Number(value || 0);
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function signedSats(value) {
  const n = Math.round(Number(value || 0));
  return `${n > 0 ? "+" : ""}${n.toLocaleString()}`;
}

function dateLabel(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
}

export default function SmartTimingResults() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(
          `/public/smart-timing-results?days=${days}`
        );

        if (!cancelled) {
          setData(res.data);
        }
      } catch (err) {
        console.error(
          "Smart Timing results unavailable",
          err
        );

        if (!cancelled) {
          setError(
            "Live Smart Timing results are temporarily unavailable."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [days]);

  const summary = data?.summary;

  return (
    <main className="min-h-screen bg-zinc-950 text-white">
      <Head>
        <title>
          Live Smart Timing Results | SatStacker
        </title>

        <meta
          name="description"
          content="See live aggregate Smart Timing results compared with scheduled Bitcoin DCA across completed SatStacker buying windows."
        />

        <link
          rel="canonical"
          href="https://satstacker.app/smart-timing-results"
        />
      </Head>

      <div className="max-w-4xl mx-auto px-5 py-12">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 mb-4">
            Live SatStacker Evidence
          </p>

          <h1 className="text-3xl md:text-5xl font-black leading-tight mb-5">
            Smart Timing Results
          </h1>

          <p className="text-lg text-zinc-300 leading-8 max-w-3xl">
            Every completed Smart Timing
            window in the selected period is included.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-8">
          {OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDays(option)}
              className={
                days === option
                  ? "rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white"
                  : "rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-500"
              }
            >
              {option} days
            </button>
          ))}
        </div>

        {loading && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-8 text-zinc-400">
            Loading verified results…
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-900/50 bg-red-950/20 p-6 text-red-300">
            {error}
          </div>
        )}

        {!loading && !error && summary && (
          <>
            <section className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-7 mb-6">
              <p className="text-sm text-zinc-400 mb-2">
                Aggregate result vs scheduled DCA
              </p>

              <div className="text-4xl md:text-5xl font-black text-orange-400">
                {signedPct(summary.gain_pct)}
              </div>

              <p className="mt-3 text-zinc-300">
                {signedSats(summary.delta_sats)} sats
                across{" "}
                {summary.completed_windows.toLocaleString()}{" "}
                completed buying windows.
              </p>
            </section>

            <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric
                label="Smart Timing"
                value={`${summary.actual_sats.toLocaleString()} sats`}
              />

              <Metric
                label="Scheduled DCA"
                value={`${summary.scheduled_sats.toLocaleString()} sats`}
              />

              <Metric
                label="Better windows"
                value={summary.positive_windows}
              />

              <Metric
                label="Worse windows"
                value={summary.negative_windows}
              />
            </section>

            <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">
                What these numbers include
              </h2>

              <p className="text-zinc-400 leading-7 mb-4">
                Results include all completed
                Smart Timing windows during the selected
                period.
                Open windows are excluded because their
                scheduled-DCA comparison price does not
                exist yet.
              </p>

              <p className="text-zinc-400 leading-7">
                The benchmark asks how many sats the same
                post-SatStacker-fee buying power would
                have purchased if it had executed at the
                scheduled end of each buying window.
              </p>
            </section>

            <section className="rounded-2xl border border-zinc-800 p-6 mb-8">
              <h2 className="text-xl font-bold mb-5">
                Results by cadence
              </h2>

              <div className="space-y-4">
                {Object.entries(
                  data.by_cadence || {}
                ).map(([cadence, item]) => (
                  <div
                    key={cadence}
                    className="flex items-center justify-between gap-4 border-b border-zinc-800 pb-4 last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="font-semibold capitalize">
                        {cadence}
                      </div>
                      <div className="text-sm text-zinc-500">
                        {item.completed_windows} completed windows
                      </div>
                    </div>

                    <div className="font-bold text-right">
                      {signedPct(item.gain_pct)}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="text-sm text-zinc-500 leading-6 mb-10">
              Period:{" "}
              {dateLabel(data.period?.start)} –{" "}
              {dateLabel(data.period?.end)}
              <br />
              Last updated:{" "}
              {dateLabel(data.updated_at)}
              <br />
              Historical results do not guarantee future
              results.
            </section>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/blog/smart-dca-vs-regular-dca"
                className="rounded-xl bg-orange-500 px-5 py-3 text-center font-bold text-white hover:bg-orange-600"
              >
                Read the 3-Month Case Study →
              </Link>

              <Link
                to="/calculator"
                className="rounded-xl border border-zinc-700 px-5 py-3 text-center font-bold text-white hover:border-zinc-500"
              >
                Try the DCA Calculator
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
        {label}
      </div>

      <div className="text-xl font-bold">
        {value}
      </div>
    </div>
  );
}